import React from 'react';
import EmptyState from '../EmptyState';
import './ExpandableCardList.css';

interface TableHeader {
  label: string;
  hideMobile?: boolean;
}

interface ExpandableCardListProps<T> {
  data: T[];
  isLoading: boolean;
  error: string;
  emptyMessage: string;
  expandedItems: Set<number>;
  onToggleExpanded: (index: number) => void;
  renderTitle: (item: T, index: number) => string;
  renderProgressIndicator: (item: T) => React.ReactNode;
  tableHeaders: TableHeader[];
  renderTableRow: (item: T, index: number) => React.ReactNode;
  hasExpandableContent?: (item: T) => boolean;
}

const ExpandableCardList = <T,>({
  data,
  isLoading,
  error,
  emptyMessage,
  expandedItems,
  onToggleExpanded,
  renderTitle,
  renderProgressIndicator,
  tableHeaders,
  renderTableRow,
  hasExpandableContent = () => true,
}: ExpandableCardListProps<T>) => {

  
  return (
    <div className="h-100">
      <div>
        {isLoading && (
          <EmptyState message="Loading your progress..." animate={true} />
        )}

        {!isLoading && error && (
          <div className="alert alert-danger" role="alert">{error}</div>
        )}

        {!isLoading && !error && data.length === 0 && (
          <EmptyState message={emptyMessage} />
        )}

        {!isLoading && !error && data.length > 0 && (
          <div>
            {data.map((item, index) => {
              const isExpanded = expandedItems.has(index);
              const showContent = hasExpandableContent(item);

              return (
                <div key={index} className="card bg-dark border-secondary mb-2 p-1">
                  <div
                    className="card-header bg-dark px-2 py-1 border-0 expandable-card-header"
                    onClick={() => onToggleExpanded(index)}
                  >
                    <div className="d-flex justify-content-between align-items-center gap-2">
                      <div className="flex-shrink-0">
                        <span className="mb-0 text-white d-block expandable-card-title">
                          {renderTitle(item, index)}
                        </span>
                      </div>
                      <div className="d-flex align-items-center justify-content-end gap-1 w-100 w-md-auto">
                        {renderProgressIndicator(item)}
                      </div>
                    </div>
                  </div>

                  {showContent && (
                    <div
                      className={`expandable-card-content ${isExpanded ? 'expanded' : 'collapsed'}`}
                    >
                      <div className="card-body bg-dark border-top border-secondary p-0">
                        <table className="table table-dark table-hover mb-0 expandable-card-table" >
                          <thead>
                            <tr>
                              {tableHeaders.map((header, i) => (
                                <th key={i} className={`text-white   ${header.hideMobile ? 'hide-mobile' : ''}`}>
                                  {header.label}
                                </th>
                              ))}
                            </tr>
                          </thead>
                          <tbody>
                            {renderTableRow(item, index)}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  )}

                  {showContent && (
                    <div
                      className="text-center bg-dark expandable-card-toggle"
                      onClick={() => onToggleExpanded(index)}
                    >
                      <span className="expandable-card-toggle-icon">
                        {isExpanded ? '▲' : '▼'}
                      </span>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default ExpandableCardList;
